import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../css/LandingPage.css';
import { ANTIS_EMITISM_TYPES } from '../../constants';
import VerifiedContentAnalysis from './VerifiedContentAnalysis';
import { formatAntisemitismTypeLabel, formatDate } from '../../utils/helpers';
import EntityCard from '../MainScreen/EntityCard';

const TYPE_META = Object.fromEntries(ANTIS_EMITISM_TYPES.map((t) => [t.id, t]));
const missionIllustrationImage = '/images/landing_page.png';

/** Must match `.landing-ihra-grid { gap }` in `LandingPage.css` */
const IHRA_CAROUSEL_GAP_PX = 20;

function getIhraCarouselScrollStep(el) {
  if (!el || el.clientWidth <= 0) return 1;
  const first = el.querySelector('article');
  if (first && first.offsetWidth > 0) {
    return first.offsetWidth + IHRA_CAROUSEL_GAP_PX;
  }
  const cardW = (el.clientWidth - 2 * IHRA_CAROUSEL_GAP_PX) / 3;
  return Math.max(1, cardW + IHRA_CAROUSEL_GAP_PX);
}

function nearestIhraPageIndex(scrollLeft, maxScroll, step, pageCount) {
  if (pageCount <= 1) return 0;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < pageCount; i++) {
    const target = Math.min(maxScroll, i * step);
    const d = Math.abs(scrollLeft - target);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

function formatKpiValue(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

const LandingPage = ({
  ihraData,
  subjectsData,
  recentEntities,
  interactions,
  defaultStatistics,
  loading,
  onReportNow,
  onNarrativeClick,
}) => {
  const navigate = useNavigate();

  const ihraLoading = loading?.ihra ?? true;
  const subjectsLoading = loading?.subjects ?? true;
  const recentEntitiesLoading = loading?.recentEntities ?? true;
  const interactionsLoading = loading?.interactions ?? true;

  const ihraTotal = ihraData
    ? Object.values(ihraData).reduce((sum, n) => sum + (Number(n) || 0), 0)
    : 0;

  const ihraScrollRef = useRef(null);
  const [ihraCanPrev, setIhraCanPrev] = useState(false);
  const [ihraCanNext, setIhraCanNext] = useState(true);
  const [ihraPageCount, setIhraPageCount] = useState(1);
  const [ihraActivePage, setIhraActivePage] = useState(0);
  const [missionExpanded, setMissionExpanded] = useState(true);
  const [ihraExpanded, setIhraExpanded] = useState(true);

  const updateIhraNavState = useCallback(() => {
    const el = ihraScrollRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    setIhraCanPrev(scrollLeft > 1);
    setIhraCanNext(scrollLeft + clientWidth < scrollWidth - 1);

    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const step = getIhraCarouselScrollStep(el);
    if (maxScroll <= 0) {
      setIhraPageCount(1);
      setIhraActivePage(0);
      return;
    }
    const pageCount = Math.ceil(maxScroll / step) + 1;
    setIhraPageCount(pageCount);
    setIhraActivePage(nearestIhraPageIndex(scrollLeft, maxScroll, step, pageCount));
  }, []);

  const scrollIhra = useCallback((dir) => {
    const el = ihraScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * getIhraCarouselScrollStep(el), behavior: 'smooth' });
  }, []);

  const scrollIhraToPage = useCallback((pageIndex) => {
    const el = ihraScrollRef.current;
    if (!el) return;
    const step = getIhraCarouselScrollStep(el);
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    const pageCount = Math.ceil(maxScroll / step) + 1;
    const clamped = Math.max(0, Math.min(pageCount - 1, pageIndex));
    const target = Math.min(maxScroll, clamped * step);
    el.scrollTo({ left: target, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!ihraExpanded) return undefined;
    const id = requestAnimationFrame(() => updateIhraNavState());
    return () => cancelAnimationFrame(id);
  }, [ihraExpanded, ihraData, updateIhraNavState]);

  useEffect(() => {
    if (!ihraExpanded) return undefined;
    const el = ihraScrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => updateIhraNavState());
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ihraExpanded, ihraData, updateIhraNavState]);

  const topSubjects = subjectsData
    ? Object.entries(subjectsData).sort(([, a], [, b]) => b - a)
    : [];

  return (
    <main className="landing-page">
      <section className="landing-hero">
        <div className="landing-title-block">
          <h1>Turning Data Into Action Against Antisemitism</h1>
          <p>
            CyberWell analyzes antisemitic content across major social media platforms to understand trends,
            narratives, and online impact.
          </p>
        </div>

        <div className="landing-alert">
          <strong>New here?</strong>
          <span>See the data below, key narratives, or</span>
          <Link to="/entities">explore our database</Link>
          <span>of antisemitic content.</span>
        </div>

        <div className="landing-kpis">
          <article className="landing-kpi">
            <div className="landing-kpi-label">Identified Content</div>
            <div className="landing-kpi-value">
              {interactionsLoading ? <span className="spinner-small" /> : interactions?.total_analytics_count != null ? formatKpiValue(interactions.total_analytics_count) : '—'}
            </div>
            <div className="landing-kpi-note">Total count of analyzed content</div>
          </article>
          <article className="landing-kpi">
            <div className="landing-kpi-label">Total Reach</div>
            <div className="landing-kpi-value">
              {interactionsLoading ? <span className="spinner-small" /> : interactions?.impression_count != null ? formatKpiValue(interactions.impression_count) : '—'}
            </div>
            <div className="landing-kpi-note">Estimated impressions across platforms</div>
          </article>
          <article className="landing-kpi">
            <div className="landing-kpi-label">Total Engagement</div>
            <div className="landing-kpi-value">
              {interactionsLoading ? <span className="spinner-small" /> : interactions?.engagement_count != null ? formatKpiValue(interactions.engagement_count) : '—'}
            </div>
            <div className="landing-kpi-note">Combined likes, shares, and comments</div>
          </article>
        </div>
      </section>

      <section className="landing-section-card">
        <h2 className="landing-mission-head">
          <button
            type="button"
            className="landing-mission-head-btn"
            onClick={() => setMissionExpanded((v) => !v)}
            aria-expanded={missionExpanded}
            aria-controls="landing-mission-panel"
          >
            <span className="landing-mission-head-label">Our Mission</span>
            <span
              className={`landing-mission-chevron${missionExpanded ? '' : ' landing-mission-chevron--collapsed'}`}
              aria-hidden="true"
            />
          </button>
        </h2>
        <div
          id="landing-mission-panel"
          className="landing-mission-content"
          hidden={!missionExpanded}
        >
          <div>
            <p>
              We are dedicated to optimizing the fight against online antisemitism through technology, transparency,
              and compliance. CyberWell identifies antisemitic content, tracks emerging hate narratives, hashtags,
              and trends, and shares real-time alerts with major social media and tech platforms&apos; safety teams globally.
            </p>
            <p>
              <strong>Help us make an impact.</strong> Report antisemitic content and strengthen the response to online hate!
            </p>
            <button
              type="button"
              className="landing-orange-btn"
              onClick={() => onReportNow?.()}
            >
              Report Now
            </button>
          </div>
          <div className="landing-mission-illustration">
            <img src={missionIllustrationImage} alt="" aria-hidden="true" />
          </div>
        </div>
      </section>

      <section className="landing-section-card">
        <VerifiedContentAnalysis
          prefetchedStatistics={defaultStatistics}
          prefetchedStatisticsLoading={loading?.defaultStatistics ?? true}
        />
      </section>

      <section className="landing-section-card landing-ihra-section-card">
        <h2 className="landing-mission-head landing-ihra-top-head">
          <button
            type="button"
            className="landing-mission-head-btn"
            onClick={() => setIhraExpanded((v) => !v)}
            aria-expanded={ihraExpanded}
            aria-controls="landing-ihra-panel"
          >
            <span className="landing-mission-head-label">How We Define Antisemitism</span>
            <span
              className={`landing-mission-chevron${ihraExpanded ? '' : ' landing-mission-chevron--collapsed'}`}
              aria-hidden="true"
            />
          </button>
        </h2>
        <div
          id="landing-ihra-panel"
          className="landing-ihra-body"
          hidden={!ihraExpanded}
        >
          <div className="landing-ihra-about">
            <p>
              The International Holocaust Remembrance Alliance (IHRA) working definition provides a framework for identifying antisemitic content.
              {' '}
              CyberWell also identifies emerging forms of Jew-hatred not strictly covered in the working definition.
              {' '}
              The categories in the working definition classify different manifestations of antisemitism, from historical denialism to contemporary conspiracy theories.
              <br />
              <a href="https://holocaustremembrance.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Learn more about the IHRA definition.</a>
            </p>
            <div className="landing-ihra-logo" aria-hidden="true">
              <img src="/images/ihra.png" alt="" />
            </div>
          </div>
          <div className="landing-ihra-line" />
          <div className="landing-ihra-categories">
            <div className="landing-ihra-head">
              <h3>IHRA Definition Categories</h3>
              <div className="landing-ihra-head-right">
                <div className="landing-ihra-total">Total posts reported by IHRA categories: <strong>{ihraTotal.toLocaleString()}</strong></div>
                <div className="landing-ihra-nav">
                  <button
                    type="button"
                    className="landing-ihra-nav-btn"
                    onClick={() => scrollIhra(-1)}
                    disabled={!ihraCanPrev}
                    aria-label="Previous"
                  >&#8249;</button>
                  <button
                    type="button"
                    className="landing-ihra-nav-btn"
                    onClick={() => scrollIhra(1)}
                    disabled={!ihraCanNext}
                    aria-label="Next"
                  >&#8250;</button>
                </div>
              </div>
            </div>
            <div
              className="landing-ihra-grid"
              ref={ihraScrollRef}
              onScroll={updateIhraNavState}
            >
              {ihraLoading && !ihraData && (
                <p className="landing-ihra-loading"><span className="spinner-small" /> Loading categories…</p>
              )}
              {ihraData && Object.entries(ihraData).map(([id, count]) => {
                const meta = TYPE_META[id];
                const total = ihraTotal || 1;
                const pct = ((Number(count) / total) * 100).toFixed(1) + '%';
                return (
                  <article key={id}>
                    <div className="eg">{formatAntisemitismTypeLabel(id)}</div>
                    <h4>{meta ? meta.name : id}</h4>
                    <div className="nums">
                      <strong>{Number(count).toLocaleString()}</strong>
                      <span>|</span>
                      <strong>{pct}</strong>
                    </div>
                    <p>{meta ? meta.description : ''}</p>
                  </article>
                );
              })}
            </div>
            {ihraData && Object.keys(ihraData).length > 0 && (
              <div
                className="landing-ihra-pagin"
                role="tablist"
                aria-label="IHRA categories carousel position"
              >
                {Array.from({ length: ihraPageCount }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === ihraActivePage}
                    aria-label={`Page ${i + 1} of ${ihraPageCount}`}
                    className={`landing-ihra-pagin-dot${i === ihraActivePage ? ' landing-ihra-pagin-dot--active' : ''}`}
                    onClick={() => scrollIhraToPage(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="landing-dual-section">
        <div className="landing-section-card landing-section-card--compact">
          <h2>Key Narratives</h2>
          <div className="landing-list">
            {subjectsLoading && !subjectsData && (
              <p className="landing-ihra-loading"><span className="spinner-small" /> Loading narratives…</p>
            )}
            {topSubjects.slice(0, 5).map(([title, count]) => (
              <button
                key={title}
                type="button"
                className="landing-list-item"
                onClick={() => onNarrativeClick?.(title)}
                title={`View entities for narrative: ${title}`}
              >
                <h4>{title}</h4>
                <span>{Number(count).toLocaleString()} instances identified</span>
              </button>
            ))}
          </div>
        </div>
        <div className="landing-section-card landing-section-card--compact">
          <div className="landing-posts-header">
            <h2>Recent Posts</h2>
            <button
              type="button"
              className="landing-outline-btn"
              onClick={() => navigate('/entities')}
            >
              View all analyzed posts
            </button>
          </div>
          <div className="landing-entity-list">
            {recentEntitiesLoading && (
              <p className="landing-ihra-loading"><span className="spinner-small" /> Loading posts…</p>
            )}
            {!recentEntitiesLoading && recentEntities.map((entity) => (
              <EntityCard
                key={entity.EntityId}
                entity={entity}
                isSelected={false}
                onSelect={(id) => navigate(`/entities?entity=${id}`)}
                formatDate={formatDate}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="landing-explore">
        <h2>Explore Our Data &amp; Take Action</h2>
        <p>Search through thousands of documented cases of antisemitic content.</p>
        <p>View detailed analysis, platform information, and classification data.</p>
        <div className="landing-explore-actions">
          <button
            type="button"
            onClick={() => onReportNow?.()}
            aria-haspopup="dialog"
          >
            Alert Us!
          </button>
          <Link to="/entities">Browse Analyzed Posts</Link>
          <a href="https://cyberwell.org/about-us/" target="_blank" rel="noopener noreferrer">Learn About Our Work</a>
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
